import {colorPrimitive} from '../../styles/primitives/colorPrimitive';

export interface BackgroundSemanticStyles {
  base: string;
  'base-secondary': string;

  surface: {
    primary: string;
    primaryHover: string;
    primaryActive: string;

    secondary: string;
    secondaryHover: string;

    tertiary: string;
    tertiaryHover: string;
    tertiaryActive: string;

    inverse: string;

    info: string;
    infoSecondary: string;
    infoHover: string;

    success: string;
    successSecondary: string;
    successHover: string;

    warning: string;
    warningSecondary: string;
    warningHover: string;

    danger: string;
    dangerSecondary: string;
    dangerHover: string;

    brand: string;
    brandSecondary: string;
    brandHover: string;

    highlight: string;
    highlightHover: string;

    lowlightHover: string;

    overlay: string;
  };

  fill: {
    primary: string;
    primaryHover: string;
    primaryDisabled: string;
    primaryPressed: string;

    secondary: string;
    secondaryHover: string;
    secondaryDisabled: string;
    secondaryPressed: string;

    tertiary: string;
    tertiaryHover: string;
    tertiaryDisabled: string;
    tertiaryPressed: string;

    success: string;
    successHover: string;
    successPressed: string;
    successDisabled: string;

    danger: string;
    dangerHover: string;
    dangerPressed: string;
    dangerDisabled: string;

    brand: string;
    brandHover: string;
    brandPressed: string;
    brandDisabled: string;

    warning: string;
    warningHover: string;
    warningPressed: string;
    warningDisabled: string;

    inverse: string;
    highlight: string;
    highlightSecondary: string;
    highlightSecondaryActive: string;

    loading: string;
    loadingProgress: string;
  };
}

export const lightBackgroundSemanticStyles = {
  base: colorPrimitive.gray[200],
  'base-secondary': colorPrimitive.gray[100],

  surface: {
    primary: colorPrimitive.gray.white,
    primaryHover: colorPrimitive.gray['50'],
    primaryActive: colorPrimitive.gray['100'],

    secondary: colorPrimitive.gray['100'],
    secondaryHover: colorPrimitive.gray['200'],

    tertiary: colorPrimitive.alpha.transparent,
    tertiaryHover: colorPrimitive.alpha.black.black100,
    tertiaryActive: colorPrimitive.alpha.black.black200,

    inverse: colorPrimitive.gray['1000'],

    info: colorPrimitive.blue['200'],
    infoSecondary: colorPrimitive.blue['100'],
    infoHover: colorPrimitive.blue['300'],

    success: colorPrimitive.green['200'],
    successSecondary: colorPrimitive.green['100'],
    successHover: colorPrimitive.green['300'],

    warning: colorPrimitive.yellow['200'],
    warningSecondary: colorPrimitive.yellow['100'],
    warningHover: colorPrimitive.yellow['300'],

    danger: colorPrimitive.red['200'],
    dangerSecondary: colorPrimitive.red['100'],
    dangerHover: colorPrimitive.red['300'],

    brand: colorPrimitive.purple['200'],
    brandSecondary: colorPrimitive.purple['100'],
    brandHover: colorPrimitive.purple['300'],

    highlight: colorPrimitive.gray.white,
    highlightHover: colorPrimitive.gray['50'],
    lowlightHover: colorPrimitive.alpha.black.black50,

    overlay: colorPrimitive.alpha.black.black1000,
  },
  fill: {
    primary: colorPrimitive.blue['600'],
    primaryHover: colorPrimitive.blue['700'],
    primaryDisabled: colorPrimitive.gray['200'],
    primaryPressed: colorPrimitive.blue['800'],

    secondary: colorPrimitive.alpha.transparent,
    secondaryHover: colorPrimitive.alpha.black.black100,
    secondaryPressed: colorPrimitive.alpha.black.black200,
    secondaryDisabled: colorPrimitive.gray['200'],

    tertiary: colorPrimitive.alpha.transparent,
    tertiaryHover: colorPrimitive.alpha.black.black100,
    tertiaryDisabled: colorPrimitive.alpha.transparent,
    tertiaryPressed: colorPrimitive.alpha.black.black200,

    success: colorPrimitive.green['600'],
    successHover: colorPrimitive.green['700'],
    successPressed: colorPrimitive.green['800'],
    successDisabled: colorPrimitive.green['200'],

    danger: colorPrimitive.red['600'],
    dangerHover: colorPrimitive.red['700'],
    dangerPressed: colorPrimitive.red['800'],
    dangerDisabled: colorPrimitive.red['200'],

    brand: colorPrimitive.purple['600'],
    brandHover: colorPrimitive.purple['700'],
    brandPressed: colorPrimitive.purple['800'],
    brandDisabled: colorPrimitive.purple['200'],

    warning: colorPrimitive.yellow['300'],
    warningHover: colorPrimitive.yellow['400'],
    warningPressed: colorPrimitive.yellow['500'],
    warningDisabled: colorPrimitive.yellow['200'],

    inverse: colorPrimitive.gray['900'],
    highlight: colorPrimitive.yellow.highlight,
    highlightSecondary: colorPrimitive.orange['300'],
    highlightSecondaryActive: colorPrimitive.orange['400'],

    loading: colorPrimitive.gray['600'],
    loadingProgress: colorPrimitive.gray['800'],
  },
} as const satisfies BackgroundSemanticStyles;

export const darkBackgroundSemanticStyles = {
  base: colorPrimitive.gray.black,
  'base-secondary': colorPrimitive.alpha.white.white100,

  surface: {
    primary: colorPrimitive.gray['1000'],
    primaryHover: colorPrimitive.gray['950'],
    primaryActive: colorPrimitive.gray['900'],

    secondary: colorPrimitive.gray['1050'],
    secondaryHover: colorPrimitive.gray['1000'],

    tertiary: colorPrimitive.alpha.transparent,
    tertiaryHover: colorPrimitive.alpha.white.white100,
    tertiaryActive: colorPrimitive.alpha.white.white200,

    inverse: colorPrimitive.gray['200'],

    info: colorPrimitive.blue['900'],
    infoSecondary: colorPrimitive.blue['1000'],
    infoHover: colorPrimitive.blue['800'],

    success: colorPrimitive.green['900'],
    successSecondary: colorPrimitive.green['1000'],
    successHover: colorPrimitive.green['800'],

    warning: colorPrimitive.yellow['900'],
    warningSecondary: colorPrimitive.yellow['1000'],
    warningHover: colorPrimitive.yellow['800'],

    danger: colorPrimitive.red['900'],
    dangerSecondary: colorPrimitive.red['1000'],
    dangerHover: colorPrimitive.red['800'],

    brand: colorPrimitive.purple['900'],
    brandSecondary: colorPrimitive.purple['1000'],
    brandHover: colorPrimitive.purple['800'],

    highlight: colorPrimitive.gray['950'],
    highlightHover: colorPrimitive.gray['900'],
    lowlightHover: colorPrimitive.alpha.white.white50,

    overlay: colorPrimitive.alpha.black.black2000,
  },
  fill: {
    primary: colorPrimitive.blue['700'],
    primaryHover: colorPrimitive.blue['600'],
    primaryPressed: colorPrimitive.blue['500'],
    primaryDisabled: colorPrimitive.gray['800'],

    secondary: colorPrimitive.alpha.transparent,
    secondaryHover: colorPrimitive.alpha.white.white100,
    secondaryPressed: colorPrimitive.alpha.white.white200,
    secondaryDisabled: colorPrimitive.gray['800'],

    tertiary: colorPrimitive.alpha.transparent,
    tertiaryHover: colorPrimitive.alpha.white.white200,
    tertiaryPressed: colorPrimitive.alpha.white.white300,
    tertiaryDisabled: colorPrimitive.alpha.transparent,

    success: colorPrimitive.green['600'],
    successHover: colorPrimitive.green['700'],
    successPressed: colorPrimitive.green['800'],
    successDisabled: colorPrimitive.green['900'],

    danger: colorPrimitive.red['600'],
    dangerHover: colorPrimitive.red['700'],
    dangerPressed: colorPrimitive.red['800'],
    dangerDisabled: colorPrimitive.red['900'],

    brand: colorPrimitive.purple['600'],
    brandHover: colorPrimitive.purple['700'],
    brandPressed: colorPrimitive.purple['800'],
    brandDisabled: colorPrimitive.purple['900'],

    warning: colorPrimitive.yellow['300'],
    warningHover: colorPrimitive.yellow['400'],
    warningPressed: colorPrimitive.yellow['500'],
    warningDisabled: colorPrimitive.yellow['900'],

    inverse: colorPrimitive.gray['200'],
    highlight: colorPrimitive.yellow.highlight,
    highlightSecondary: colorPrimitive.orange['400'],
    highlightSecondaryActive: colorPrimitive.orange['500'],

    loading: colorPrimitive.gray['600'],
    loadingProgress: colorPrimitive.gray['800'],
  },
} as const satisfies BackgroundSemanticStyles;

export const redesignedLightBackgroundSemanticStyles = {
  ...lightBackgroundSemanticStyles,
  surface: {
    ...lightBackgroundSemanticStyles.surface,
    primary: colorPrimitive.gray['50'],
    primaryHover: colorPrimitive.gray['100'],
    info: colorPrimitive.blurple['200'],
    infoSecondary: colorPrimitive.blurple['100'],
    infoHover: colorPrimitive.blurple['300'],
  },
  fill: {
    ...lightBackgroundSemanticStyles.fill,
    primary: colorPrimitive.blurple[600],
    primaryHover: colorPrimitive.blurple[700],
    primaryPressed: colorPrimitive.blurple[800],
  },
} as const satisfies BackgroundSemanticStyles;

export const redesignedDarkBackgroundSemanticStyles = {
  ...darkBackgroundSemanticStyles,
  surface: {
    ...darkBackgroundSemanticStyles.surface,
    primary: colorPrimitive.gray['1000'],
    primaryHover: colorPrimitive.gray['950'],
    primaryActive: colorPrimitive.gray['900'],
    info: colorPrimitive.blurple['900'],
    infoSecondary: colorPrimitive.blurple['1000'],
    infoHover: colorPrimitive.blurple['800'],
  },
  fill: {
    ...darkBackgroundSemanticStyles.fill,
    primary: colorPrimitive.blurple[700],
    primaryHover: colorPrimitive.blurple[600],
    primaryPressed: colorPrimitive.blurple[500],
  },
} as const satisfies BackgroundSemanticStyles;
